import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Save, Bell, Globe, Users, Settings as SettingsIcon, Activity, LogOut, Wrench, Bus, Calendar, BarChart3, Cog, ClipboardList, Link2, Download, DollarSign, Search, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NotificationSettings from './admin/NotificationSettings';
import UserManagement from './admin/UserManagement';
import CriticalParameters from './admin/CriticalParameters';
import ServerManagement from './admin/ServerManagement';
import MechanicCoefficients from './admin/MechanicCoefficients';
import RepairQueue from './admin/RepairQueue';
import AuditLogsPage from './AuditLogsPage';
import BusCalendar from './admin/BusCalendar';
import BusAnalytics from './admin/BusAnalytics';
import SystemModules from './admin/SystemModules';
import ProjectTasks from './admin/ProjectTasks';
import DriverBusSettings from './admin/DriverBusSettings';
import AppDownload from './admin/AppDownload';
import SalarySettings from './admin/SalarySettings';
import UniversalSearch from './admin/UniversalSearch';
import GeliosIntegration from './admin/GeliosIntegration';
import CalendarV2 from './admin/CalendarV2';
import { useModules } from '@/contexts/ModulesContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState({
    telegram_bot_token: '',
    telegram_chat_id: '-1003277401743',
    whatsapp_enabled: false,
    whatsapp_number: '',
    viber_enabled: false,
    viber_token: '',
    email_enabled: false,
    email_address: '',
    smtp_server: '',
    smtp_port: '',
    smtp_username: '',
    smtp_password: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin';
  const { isModuleEnabled } = useModules();

  // Calculate the first available tab based on enabled modules
  const getDefaultTab = () => {
    if (isModuleEnabled('user_management')) return 'users';
    if (isModuleEnabled('bus_parameters')) return 'parameters';
    if (isModuleEnabled('audit_log')) return 'audit';
    return 'server'; // Server is always available
  };

  useEffect(() => {
    fetchUserAndSettings();
  }, []);

  const fetchUserAndSettings = async () => {
    try {
      const [userRes, settingsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, getAuthHeaders()),
        axios.get(`${API}/notification-settings`, getAuthHeaders())
      ]);
      setUser(userRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification-settings`, settings, getAuthHeaders());
      toast.success('Настройки сохранены');
    } catch (error) {
      toast.error('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-200 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-200 to-indigo-100" data-testid="admin-panel">
      {/* Modern Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Brand */}
            <div className="flex items-center gap-3">
              <Button
                data-testid="back-button"
                onClick={() => navigate('/')}
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30">
                <SettingsIcon className="w-5 h-5 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-white">Админ панель</h1>
                <p className="text-xs text-slate-400">Управление системой</p>
              </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2">
              <Button 
                data-testid="save-settings-button"
                onClick={handleSave}
                disabled={saving}
                size="sm"
                className="bg-indigo-500 hover:bg-indigo-600 text-white font-semibold"
              >
                <Save className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{saving ? 'Сохранение...' : 'Сохранить'}</span>
              </Button>
              <Button 
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-8">
        <Tabs defaultValue={getDefaultTab()} className="w-full">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Левая часть - Навигация (только на десктопе) */}
            <div className="hidden md:block md:w-64 flex-shrink-0">
              <div className="sticky top-24">
                <div className="bg-white/80 backdrop-blur-xl shadow-xl rounded-2xl p-4 border border-gray-100">
                  <div className="mb-4 px-3">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Навигация</h3>
                  </div>
                  <TabsList className="flex flex-col w-full h-auto bg-transparent p-0 space-y-1">
                    {isModuleEnabled('user_management') && (
                      <TabsTrigger 
                        value="users" 
                        className="w-full justify-start data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                      >
                        <Users className="w-4 h-4 mr-3" />
                        <span className="text-sm">Пользователи</span>
                      </TabsTrigger>
                    )}
                    {isModuleEnabled('bus_parameters') && (
                      <TabsTrigger 
                        value="parameters" 
                        className="w-full justify-start data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                      >
                        <SettingsIcon className="w-4 h-4 mr-3" />
                        <span className="text-sm">Параметры</span>
                      </TabsTrigger>
                    )}
                    {isModuleEnabled('audit_log') && (
                      <TabsTrigger 
                        value="audit" 
                        className="w-full justify-start data-[state=active]:bg-purple-500 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                      >
                        <Activity className="w-4 h-4 mr-3" />
                        <span className="text-sm">Аудит</span>
                      </TabsTrigger>
                    )}
                    <TabsTrigger 
                      value="server" 
                      className="w-full justify-start data-[state=active]:bg-pink-500 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                    >
                      <Globe className="w-4 h-4 mr-3" />
                      <span className="text-sm">Сервер</span>
                    </TabsTrigger>
                    {(isModuleEnabled('notifications_telegram') || isModuleEnabled('notifications_email')) && (
                      <TabsTrigger 
                        value="notifications" 
                        className="w-full justify-start data-[state=active]:bg-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                      >
                        <Bell className="w-4 h-4 mr-3" />
                        <span className="text-sm">Уведомления</span>
                      </TabsTrigger>
                    )}
                    {isModuleEnabled('mechanic') && (
                      <>
                        <TabsTrigger 
                          value="repairs" 
                          className="w-full justify-start data-[state=active]:bg-red-500 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                        >
                          <Bus className="w-4 h-4 mr-3" />
                          <span className="text-sm">Ремонты</span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="mechanic" 
                          className="w-full justify-start data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                        >
                          <Wrench className="w-4 h-4 mr-3" />
                          <span className="text-sm">Механик</span>
                        </TabsTrigger>
                      </>
                    )}
                    {isSuperAdmin && (
                      <>
                        {isModuleEnabled('bus_calendar') && (
                          <TabsTrigger 
                            value="calendar" 
                            className="w-full justify-start data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                          >
                            <Calendar className="w-4 h-4 mr-3" />
                            <span className="text-sm">Календарь</span>
                          </TabsTrigger>
                        )}
                        {isModuleEnabled('bus_analytics') && (
                          <TabsTrigger 
                            value="analytics" 
                            className="w-full justify-start data-[state=active]:bg-indigo-500 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                          >
                            <BarChart3 className="w-4 h-4 mr-3" />
                            <span className="text-sm">Аналитика</span>
                          </TabsTrigger>
                        )}
                        <TabsTrigger 
                          value="modules" 
                          className="w-full justify-start data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                        >
                          <Cog className="w-4 h-4 mr-3" />
                          <span className="text-sm">Модули</span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="driver-bus" 
                          className="w-full justify-start data-[state=active]:bg-teal-500 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                        >
                          <Link2 className="w-4 h-4 mr-3" />
                          <span className="text-sm">Привязки</span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="salary" 
                          className="w-full justify-start data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                        >
                          <DollarSign className="w-4 h-4 mr-3" />
                          <span className="text-sm">Зарплаты</span>
                        </TabsTrigger>
                        {isModuleEnabled('project_tasks') && (
                          <TabsTrigger 
                            value="tasks" 
                            data-value="tasks"
                            className="w-full justify-start data-[state=active]:bg-pink-600 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                          >
                            <ClipboardList className="w-4 h-4 mr-3" />
                            <span className="text-sm">Задачи</span>
                          </TabsTrigger>
                        )}
                        <TabsTrigger 
                          value="app-download" 
                          data-value="app-download"
                          className="w-full justify-start data-[state=active]:bg-cyan-600 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                        >
                          <Download className="w-4 h-4 mr-3" />
                          <span className="text-sm">Приложение</span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="search" 
                          data-value="search"
                          className="w-full justify-start data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                        >
                          <Search className="w-4 h-4 mr-3" />
                          <span className="text-sm">Поиск</span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="gelios" 
                          data-value="gelios"
                          className="w-full justify-start data-[state=active]:bg-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                        >
                          <Truck className="w-4 h-4 mr-3" />
                          <span className="text-sm">Gelios</span>
                        </TabsTrigger>
                        <TabsTrigger 
                          value="calendar-v2" 
                          data-value="calendar-v2"
                          className="w-full justify-start data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg font-medium py-3 px-4 rounded-xl transition-all duration-200 hover:bg-gray-100 text-gray-600"
                        >
                          <Calendar className="w-4 h-4 mr-3" />
                          <span className="text-sm">Календарь v2</span>
                        </TabsTrigger>
                      </>
                    )}
                  </TabsList>
                </div>
              </div>
            </div>

            {/* Правая часть - Контент */}
            <div className="flex-1 min-w-0">
              {isModuleEnabled('user_management') && (
                <TabsContent value="users" className="mt-0">
                  <UserManagement />
                </TabsContent>
              )}

              {isModuleEnabled('bus_parameters') && (
                <TabsContent value="parameters" className="mt-0">
                  <CriticalParameters />
                </TabsContent>
              )}

              {isModuleEnabled('audit_log') && (
                <TabsContent value="audit" className="mt-0">
                  <AuditLogsPage />
                </TabsContent>
              )}

              <TabsContent value="server" className="mt-0">
                <ServerManagement />
              </TabsContent>

              {(isModuleEnabled('notifications_telegram') || isModuleEnabled('notifications_email')) && (
                <TabsContent value="notifications" className="mt-0 space-y-6">
                  <NotificationSettings settings={settings} setSettings={setSettings} />
                </TabsContent>
              )}

              {isModuleEnabled('mechanic') && (
                <>
                  <TabsContent value="repairs" className="mt-0">
                    <RepairQueue />
                  </TabsContent>

                  <TabsContent value="mechanic" className="mt-0">
                    <MechanicCoefficients />
                  </TabsContent>
                </>
              )}

              {isSuperAdmin && (
                <>
                  {isModuleEnabled('bus_calendar') && (
                    <TabsContent value="calendar" className="mt-0">
                      <BusCalendar />
                    </TabsContent>
                  )}

                  {isModuleEnabled('bus_analytics') && (
                    <TabsContent value="analytics" className="mt-0">
                      <BusAnalytics />
                    </TabsContent>
                  )}

                  <TabsContent value="modules" className="mt-0">
                    <SystemModules />
                  </TabsContent>

                  <TabsContent value="driver-bus" className="mt-0">
                    <DriverBusSettings />
                  </TabsContent>

                  <TabsContent value="salary" className="mt-0">
                    <SalarySettings />
                  </TabsContent>

                  {isModuleEnabled('project_tasks') && (
                    <TabsContent value="tasks" className="mt-0">
                      <ProjectTasks />
                    </TabsContent>
                  )}

                  <TabsContent value="app-download" className="mt-0">
                    <AppDownload />
                  </TabsContent>

                  <TabsContent value="search" className="mt-0">
                    <UniversalSearch />
                  </TabsContent>

                  <TabsContent value="gelios" className="mt-0">
                    <GeliosIntegration />
                  </TabsContent>

                  <TabsContent value="calendar-v2" className="mt-0">
                    <CalendarV2 />
                  </TabsContent>
                </>
              )}
            </div>
          </div>

          {/* Мобильная нижняя навигация */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-800 border-t border-slate-700 shadow-2xl z-50">
            <TabsList className="flex w-full h-16 bg-transparent p-0 overflow-x-auto">
              {isModuleEnabled('user_management') && (
                <TabsTrigger 
                  value="users" 
                  className="flex flex-col items-center justify-center gap-1 data-[state=active]:text-blue-400 text-slate-400 rounded-none min-w-[60px] px-2"
                >
                  <Users className="w-5 h-5" />
                  <span className="text-[9px] font-medium">Пользов.</span>
                </TabsTrigger>
              )}
              {isModuleEnabled('bus_parameters') && (
                <TabsTrigger 
                  value="parameters" 
                  className="flex flex-col items-center justify-center gap-1 data-[state=active]:text-orange-400 text-slate-400 rounded-none min-w-[60px] px-2"
                >
                  <SettingsIcon className="w-5 h-5" />
                  <span className="text-[9px] font-medium">Парам.</span>
                </TabsTrigger>
              )}
              {isModuleEnabled('audit_log') && (
                <TabsTrigger 
                  value="audit" 
                  className="flex flex-col items-center justify-center gap-1 data-[state=active]:text-purple-400 text-slate-400 rounded-none min-w-[60px] px-2"
                >
                  <Activity className="w-5 h-5" />
                  <span className="text-[9px] font-medium">Аудит</span>
                </TabsTrigger>
              )}
              <TabsTrigger 
                value="server" 
                className="flex flex-col items-center justify-center gap-1 data-[state=active]:text-pink-400 text-slate-400 rounded-none min-w-[60px] px-2"
              >
                <Globe className="w-5 h-5" />
                <span className="text-[9px] font-medium">Сервер</span>
              </TabsTrigger>
              {(isModuleEnabled('notifications_telegram') || isModuleEnabled('notifications_email')) && (
                <TabsTrigger 
                  value="notifications" 
                  className="flex flex-col items-center justify-center gap-1 data-[state=active]:text-cyan-400 text-slate-400 rounded-none min-w-[60px] px-2"
                >
                  <Bell className="w-5 h-5" />
                  <span className="text-[9px] font-medium">Уведом.</span>
                </TabsTrigger>
              )}
              {isModuleEnabled('mechanic') && (
                <>
                  <TabsTrigger 
                    value="repairs" 
                    className="flex flex-col items-center justify-center gap-1 data-[state=active]:text-red-400 text-slate-400 rounded-none min-w-[60px] px-2"
                  >
                    <Bus className="w-5 h-5" />
                    <span className="text-[9px] font-medium">Ремонты</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="mechanic" 
                    className="flex flex-col items-center justify-center gap-1 data-[state=active]:text-orange-400 text-slate-400 rounded-none min-w-[60px] px-2"
                  >
                    <Wrench className="w-5 h-5" />
                    <span className="text-[9px] font-medium">Механик</span>
                  </TabsTrigger>
                </>
              )}
              {isSuperAdmin && (
                <>
                  {isModuleEnabled('bus_calendar') && (
                    <TabsTrigger 
                      value="calendar" 
                      className="flex flex-col items-center justify-center gap-1 data-[state=active]:text-green-400 text-slate-400 rounded-none min-w-[60px] px-2"
                    >
                      <Calendar className="w-5 h-5" />
                      <span className="text-[9px] font-medium">Календ.</span>
                    </TabsTrigger>
                  )}
                  {isModuleEnabled('bus_analytics') && (
                    <TabsTrigger 
                      value="analytics" 
                      className="flex flex-col items-center justify-center gap-1 data-[state=active]:text-indigo-400 text-slate-400 rounded-none min-w-[60px] px-2"
                    >
                      <BarChart3 className="w-5 h-5" />
                      <span className="text-[9px] font-medium">Аналит.</span>
                    </TabsTrigger>
                  )}
                  <TabsTrigger 
                    value="modules" 
                    className="flex flex-col items-center justify-center gap-1 data-[state=active]:text-purple-400 text-slate-400 rounded-none min-w-[60px] px-2"
                  >
                    <Cog className="w-5 h-5" />
                    <span className="text-[9px] font-medium">Модули</span>
                  </TabsTrigger>
                  {isModuleEnabled('project_tasks') && (
                    <TabsTrigger 
                      value="tasks" 
                      data-value="tasks"
                      className="flex flex-col items-center justify-center gap-1 data-[state=active]:text-pink-400 text-slate-400 rounded-none min-w-[60px] px-2"
                    >
                      <ClipboardList className="w-5 h-5" />
                      <span className="text-[9px] font-medium">Задачи</span>
                    </TabsTrigger>
                  )}
                </>
              )}
            </TabsList>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
